import React, { useEffect, useState, useRef } from 'react';
import { getPDBURL } from '../services/api';
import * as $3Dmol from '3dmol'; // Import 3Dmol.js like the drug page

interface PDBViewerProps {
    pdbId: string;
    geneId: string;
    isVisible: boolean;
    onClose: () => void;
}

const PDBViewer: React.FC<PDBViewerProps> = ({ pdbId, geneId, isVisible, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pdbUrl, setPdbUrl] = useState<string>('');
    const viewerRef = useRef<HTMLDivElement>(null);
    const viewer3DRef = useRef<any>(null);

    useEffect(() => {
        if (isVisible && pdbId) {
            loadPDBData();
        }
    }, [isVisible, pdbId]);

    // Cleanup 3Dmol viewer on unmount
    useEffect(() => {
        return () => {
            if (viewer3DRef.current) {
                try {
                    // 3Dmol doesn't have a dispose method, just clear the container
                    if (viewerRef.current) {
                        viewerRef.current.innerHTML = '';
                    }
                } catch (err) {
                    console.warn('Error cleaning up 3Dmol viewer:', err);
                }
            }
        };
    }, []);

    const loadPDBData = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Get preassigned URL for PDB file from S3
            const response = await getPDBURL(pdbId);
            if (!response.success) {
                throw new Error(response.error || 'Failed to get PDB URL');
            }
            
            const { url } = response;
            console.log('🎯 Got PDB URL from API:', url);
            setPdbUrl(url);
            
            // Initialize 3Dmol viewer after URL is loaded
            setTimeout(() => {
                initialize3DmolViewer(url);
            }, 100);
            
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load PDB data');
        } finally {
            setLoading(false);
        }
    };

    const initialize3DmolViewer = (pdbUrl: string) => {
        if (!viewerRef.current) return;

        // Clear previous viewer
        viewerRef.current.innerHTML = '';

        // Don't render if no PDB URL is provided
        if (!pdbUrl) {
            console.log('No PDB URL provided, skipping 3D viewer');
            return;
        }

        try {
            const config = { backgroundColor: 'white' }; // Viewer configuration
            const viewer = $3Dmol.createViewer(viewerRef.current, config); // Create the 3Dmol viewer
            viewer3DRef.current = viewer;

            // Fetch the PDB file
            fetch(pdbUrl)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`Failed to fetch PDB file: ${response.status} ${response.statusText}`);
                    }
                    return response.text();
                })
                .then((data) => {
                    console.log('🎯 PDB content fetched, length:', data.length);
                    viewer.addModel(data, 'pdb'); // Add the PDB model
                    viewer.setStyle({}, { cartoon: { color: 'spectrum' } }); // Set the style (cartoon)
                    viewer.zoomTo(); // Zoom to fit the model
                    viewer.render(); // Render the model
                    console.log('🎯 Successfully loaded PDB file with 3Dmol');
                })
                .catch((error) => {
                    console.error('🎯 Error loading PDB file:', error);
                    setError('Failed to load 3D structure: ' + error.message);
                });

            // Handle resize events
            const handleResize = () => {
                if (viewer3DRef.current) {
                    viewer3DRef.current.resize(); // Resize the viewer when the window is resized
                }
            };

            window.addEventListener('resize', handleResize);

            // Cleanup event listeners on component unmount
            return () => {
                window.removeEventListener('resize', handleResize);
            };

        } catch (err) {
            console.error('🎯 3Dmol viewer error:', err);
            setError('Failed to initialize 3D viewer: ' + (err instanceof Error ? err.message : String(err)));
        }
    };

    if (!isVisible) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000,
            padding: window.innerWidth <= 768 ? '10px' : '20px'
        }}>
            <div style={{
                backgroundColor: 'white',
                padding: window.innerWidth <= 768 ? '15px' : '20px',
                borderRadius: '10px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                width: window.innerWidth <= 768 ? '95%' : '90%',
                maxWidth: '800px',
                height: window.innerWidth <= 768 ? '95%' : '90%',
                maxHeight: '600px',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '10px',
                    borderBottom: '1px solid #eee',
                    paddingBottom: '10px',
                    flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                    gap: window.innerWidth <= 768 ? '10px' : '0'
                }}>
                    <h3 style={{ 
                        margin: 0, 
                        color: '#333',
                        fontSize: window.innerWidth <= 768 ? '1rem' : '1.2rem',
                        textAlign: window.innerWidth <= 768 ? 'center' : 'left'
                    }}>
                        3D Protein Structure: {geneId} ({pdbId})
                    </h3>
                    <button
                        onClick={onClose}
                        style={{
                            background: '#f5f5f5',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: window.innerWidth <= 768 ? '18px' : '20px',
                            cursor: 'pointer',
                            color: '#333',
                            padding: window.innerWidth <= 768 ? '6px 10px' : '8px 12px',
                            minWidth: window.innerWidth <= 768 ? '36px' : '40px',
                            minHeight: window.innerWidth <= 768 ? '36px' : '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            // Mobile touch optimizations
                            touchAction: 'manipulation'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = '#e0e0e0';
                            e.currentTarget.style.borderColor = '#bbb';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = '#f5f5f5';
                            e.currentTarget.style.borderColor = '#ddd';
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div style={{ flex: 1, padding: '10px' }}>
                    {loading && (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            <div>Loading 3D structure...</div>
                        </div>
                    )}
                    
                    {error && (
                        <div style={{ textAlign: 'center', padding: '50px', color: 'red' }}>
                            Error: {error}
                        </div>
                    )}
                    
                    {!loading && !error && pdbUrl && (
                        <div 
                            ref={viewerRef}
                            style={{ 
                                width: '100%', 
                                height: '100%',
                                minHeight: window.innerWidth <= 768 ? '300px' : '400px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                // Mobile touch optimizations
                                touchAction: 'manipulation',
                                userSelect: 'none',
                                WebkitUserSelect: 'none'
                            }}
                        />
                    )}
                    
                    {!loading && !error && !pdbUrl && (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            No PDB data available
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PDBViewer;